import { useCallback } from 'react';

import {
    fetchOAuthMe,
    logoutOAuth,
    rebindOAuth,
    startOAuthLogin,
} from '@/lib/api/auth';
import type { MeResponse, OAuthProvider } from '@/lib/api/auth';

import { loopbackLoginBounceUrl } from '../loopbackOAuthHost';
import { useOAuthStore } from '../store';

const LAST_PROVIDER_KEY = 'inout.oauth.last_provider';

/**
 * React access to the social-login (v2-auth) session.
 *
 * Reads the mirrored session from the store and exposes the flow actions. The
 * one-time hydration (`GET /v2/auth/me`) is performed by {@link OAuthProvider};
 * this hook is safe to call from many components without duplicating fetches.
 */
export function useOAuthSession() {
    const me = useOAuthStore((s) => s.me);
    const isLoading = useOAuthStore((s) => s.isLoading);
    const setMe = useOAuthStore((s) => s.setMe);
    const setLoading = useOAuthStore((s) => s.setLoading);

    /** Re-read the canonical session; on failure assume logged-out. */
    const refresh = useCallback(async (): Promise<MeResponse | null> => {
        try {
            const next = await fetchOAuthMe();
            setMe(next);
            return next;
        } catch {
            setMe({ authenticated: false });
            return null;
        }
    }, [setMe]);

    /** Begin login — full-page redirect to the provider's consent screen. */
    const login = useCallback(
        async (provider: OAuthProvider) => {
            setLoading(true);
            try {
                // dev loopback only: each provider's OAuth client accepts exactly one
                // loopback host (telegram → 127.0.0.1, the rest → localhost). the whole
                // ceremony must run from that host — the oauth_pending cookie is minted
                // by the login call below and read back at the callback — so bounce the
                // page there FIRST; the resume param restarts this login on arrival.
                const bounce = loopbackLoginBounceUrl(provider);
                if (bounce) {
                    window.location.replace(bounce);
                    return;
                }
                sessionStorage.setItem(LAST_PROVIDER_KEY, provider);
                const { authorizeUrl } = await startOAuthLogin(provider);
                window.location.href = authorizeUrl;
            } catch (error) {
                setLoading(false);
                throw error;
            }
        },
        [setLoading],
    );

    const logout = useCallback(async () => {
        setLoading(true);
        try {
            await logoutOAuth();
        } finally {
            setMe({ authenticated: false });
            sessionStorage.removeItem(LAST_PROVIDER_KEY);
            setLoading(false);
        }
    }, [setMe, setLoading]);

    /** Re-check the on-chain custodial binding, then refresh the session. */
    const rebind = useCallback(async () => {
        const result = await rebindOAuth();
        await refresh();
        return result;
    }, [refresh]);

    return {
        me,
        isAuthenticated: !!me?.authenticated,
        provider: me?.provider ?? null,
        accountHash: me?.accountHash ?? null,
        bindingState: me?.bindingState ?? null,
        custodialWalletAddress: me?.custodialWalletAddress ?? null,
        // the owner EOA this login is bound to (`GET /v2/auth/me` -> `owner`). this is the
        // user-facing identity; the custodial wallet address is an internal detail.
        ownerAddress: me?.owner ?? null,
        displayName: me?.displayName ?? null,
        username: me?.username ?? null,
        isLoading,
        refresh,
        login,
        logout,
        rebind,
    };
}
